/**
 * fileUrlUtils.ts
 *
 * Multiple backends serve files — each on a different domain:
 *
 *   VITE_USER_BASE_URL       → loginapi.abilityglobal.io       → profile images, org logos (C# user mgmt)
 *   VITE_FILE_BASE_URL       → api.abilityglobal.io             → HR docs, payslips, contracts (C# employee mgmt)
 *   VITE_RECRUITER_BASE_URL  → recruiterapi.abilityglobal.io   → recruiter/candidate files
 *   VITE_PAYROLL_BASE_URL    → payrollapi.abilityglobal.io      → payroll documents
 *   VITE_BASIC_BASE_URL      → auth.abilityglobal.io            → NestJS /uploads/ attachments (recruiter/tickets)
 *
 * If a path already starts with http/https it is passed through unchanged.
 *
 * Usage:
 *   getUserProfileImageUrl(path)  → profile pics & org logos
 *   getHrFileUrl(path)            → HR documents, payslips, contracts
 *   getRecruiterFileUrl(path)     → recruiter/candidate files
 *   getPayrollFileUrl(path)       → payroll documents
 *   getSanitizedFileUrl(path)     → NestJS file attachments (/uploads/)
 */

// ── Helper: extract just the origin from a base URL env var ─────────────────
const originOf = (raw: string, fallback: string): string => {
  try {
    return new URL(raw).origin;
  } catch {
    try {
      return new URL(fallback).origin;
    } catch {
      return fallback;
    }
  }
};

// ── 1. C# User Management — loginapi.abilityglobal.io ───────────────────────
//    VITE_USER_BASE_URL = "https://loginapi.abilityglobal.io/api/"
const getUserBaseUrl = (): string =>
  originOf(
    (import.meta.env.VITE_USER_BASE_URL as string | undefined) ?? '',
    'https://loginapi.abilityglobal.io'
  );

// ── 2. C# Employee Management — api.abilityglobal.io ────────────────────────
//    VITE_FILE_BASE_URL = "https://api.abilityglobal.io"
const getFileBaseUrl = (): string =>
  originOf(
    (import.meta.env.VITE_FILE_BASE_URL as string | undefined) ?? '',
    'https://api.abilityglobal.io'
  );

// ── 3. Recruiter API — recruiterapi.abilityglobal.io ────────────────────────
//    VITE_RECRUITER_BASE_URL = "https://recruiterapi.abilityglobal.io/api/"
const getRecruiterBaseUrl = (): string =>
  originOf(
    (import.meta.env.VITE_RECRUITER_BASE_URL as string | undefined) ?? '',
    'https://recruiterapi.abilityglobal.io'
  );

// ── 4. Payroll API — payrollapi.abilityglobal.io ────────────────────────────
//    VITE_PAYROLL_BASE_URL = "https://payrollapi.abilityglobal.io/api/"
const getPayrollBaseUrl = (): string =>
  originOf(
    (import.meta.env.VITE_PAYROLL_BASE_URL as string | undefined) ?? '',
    'https://payrollapi.abilityglobal.io'
  );

// ── 5. NestJS backend — auth.abilityglobal.io (prod) / localhost:9010 (dev) ──
//    VITE_BASIC_BASE_URL = "https://auth.abilityglobal.io/api/v1"
export const getUploadBaseUrl = (): string =>
  originOf(
    (import.meta.env.VITE_BASIC_BASE_URL as string | undefined) ?? '',
    'https://auth.abilityglobal.io'
  );

// ── Shared path sanitizer ────────────────────────────────────────────────────
const toCleanPath = (filePath: string): string =>
  filePath.startsWith('/') ? filePath : '/' + filePath;

// ── Shared resolver ──────────────────────────────────────────────────────────
const resolveUrl = (baseUrl: string, filePath?: string): string => {
  if (!filePath) return '#';
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
  return `${baseUrl}${toCleanPath(filePath)}`;
};

/**
 * Profile images and org logos — C# User Management (loginapi.abilityglobal.io).
 * DB: /upload/ProfileImage/{guid}.png  or  /upload/logo/{fileName}
 */
export const getUserProfileImageUrl = (filePath?: string): string =>
  resolveUrl(getUserBaseUrl(), filePath);

/**
 * HR documents, payslips, contracts — C# Employee Management (api.abilityglobal.io).
 * DB: /upload/documents/{fileName}  or  /upload/payslip/{fileName}
 */
export const getHrFileUrl = (filePath?: string): string =>
  resolveUrl(getFileBaseUrl(), filePath);

/**
 * Recruiter / candidate files — Recruiter API (recruiterapi.abilityglobal.io).
 */
export const getRecruiterFileUrl = (filePath?: string): string =>
  resolveUrl(getRecruiterBaseUrl(), filePath);

/**
 * Payroll documents — Payroll API (payrollapi.abilityglobal.io).
 */
export const getPayrollFileUrl = (filePath?: string): string =>
  resolveUrl(getPayrollBaseUrl(), filePath);

/**
 * NestJS file attachments — auth.abilityglobal.io (prod) / localhost:9010 (dev).
 * DB: /uploads/{timestamp}-{fileName}
 */
export const getSanitizedFileUrl = (filePath?: string): string =>
  resolveUrl(getUploadBaseUrl(), filePath);
